import {classNames} from '@shopify/css-utilities';
import {useState, useEffect} from 'preact/hooks';

import {ShopAppIcon} from '~/components/ShopAppIcon';
import type {Toast} from '~/types/toast';

const ANIMATION_DURATION = 300;
const TOAST_DEFAULT_DURATION = 5000;

interface ShopToastElementProps extends Toast {
  onClose: (id: string) => void;
}

export const ShopToastElement = (props: ShopToastElementProps) => {
  const {
    duration = TOAST_DEFAULT_DURATION,
    onClose,
    text,
    subText,
    actionText,
    handleAction,
    id,
    variant = 'default',
  } = props;

  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    // Trigger the enter animation
    setVisible(true);

    // Set timeout for exit animation
    const showTimer = setTimeout(() => {
      if (duration !== 0) {
        setVisible(false);
        // Wait for the animation to finish before removing
        const removeTimer = setTimeout(() => {
          onClose(id);
        }, ANIMATION_DURATION);
        return () => clearTimeout(removeTimer);
      }
    }, duration);

    return () => clearTimeout(showTimer);
  }, [duration, id, onClose]);

  const textColorClassName = classNames(
    variant === 'error' ? 'text-poppy-d1' : 'text-black',
  );

  return (
    <div
      className={classNames(
        'z-max mx-auto flex w-85 transform items-center justify-start gap-x-3 rounded-md border-[0.5px] border-solid border-grayscale-l2l px-4 py-3 text-white shadow-lg transition-transform duration-300 ease-in-out sm_bottom-0 sm_max-w-none sm_rounded-none',
        visible ? 'translate-y-0' : 'translate-y-94',
        variant === 'error' ? 'bg-poppy-l2' : 'bg-white',
      )}
      data-testid="shop-toast-element"
    >
      {variant !== 'error' && (
        <ShopAppIcon className="size-8 flex-shrink-0 text-purple-primary" />
      )}
      <div className="flex flex-col overflow-hidden">
        <div
          className={classNames(
            'font-sans text-body-title-small',
            textColorClassName,
          )}
        >
          {text}
        </div>
        {subText && (
          <div
            className={classNames(
              'truncate font-sans text-caption',
              textColorClassName,
            )}
          >
            {subText}
          </div>
        )}
      </div>
      {actionText && handleAction && (
        <button
          type="button"
          aria-label={actionText}
          className={classNames(
            'ml-auto h-8 w-fit max-w-[40%] rounded-sm100 px-3 font-sans text-button-medium',
            variant === 'error'
              ? 'bg-poppy-d1 text-white'
              : 'bg-grayscale-l4 text-black',
          )}
          data-testid="toast-action-button"
          onClick={handleAction}
        >
          {actionText}
        </button>
      )}
    </div>
  );
};
